import { IconAlertTriangle, IconRefresh } from "@tabler/icons-react";
import { useClavesSatConsultadas } from "@/features/catalogos/hooks/use-claves-sat";
import type { LineaProrrateo } from "@/features/prorrateo/types";
import { useRestablecerIndicadores } from "@/features/prorrateo/hooks/use-prorrateo-mutations";
import { SolicitarAltaCatalogo } from "@/features/solicitudes-catalogo/components/SolicitarAltaCatalogo";
import { Alert, BotonExportarExcel, Card, Divider, Group, Heading, IconAction, Stack, Table, Tbody, Td, Text, Th, Thead, Tr } from "@/components/ui";
import { LineaRow } from "./LineaRow";

interface Props {
  /** Invoice FB60: el indicador de las líneas es fijo (no se elige). */
  indicadorFijo?: boolean;
  lineas: LineaProrrateo[];
  disabled: boolean;
  solicitudId: string;
  numeroSolicitud: number;
}

/**
 * Tabla con todas las líneas del prorrateo · presentacional. Es solo el
 * "envoltorio" · el comportamiento por fila vive en `<LineaRow>`.
 *
 * Si alguna línea automática tiene una clave producto/servicio SIN cuenta
 * contable mapeada (mapeo clave→cuenta · dbo.Tag legado), muestra un banner
 * para solicitar el alta al administrador (P38) · una solicitud por clave.
 */
export function LineasTable({ lineas, disabled, solicitudId, numeroSolicitud, indicadorFijo = false }: Props) {
  const restablecer = useRestablecerIndicadores(solicitudId);
  const { descripcionDe: descripcionDeClave } = useClavesSatConsultadas(lineas.map((l) => l.claveSat));
  // Descripción OFICIAL de la clave producto/servicio (catálogo SAT), NO el
  // texto libre del concepto del CFDI. Si la clave no está catalogada, no hay
  // descripción que mostrar (se muestra solo el código).

  // Claves distintas de líneas automáticas sin cuenta contable · cada una
  // origina UNA solicitud de catálogo (la cuenta se mapea por clave, no por línea).
  const clavesSinCuenta = Array.from(
    new Map(
      lineas
        .filter((l) => l.tipo !== "Manual" && !l.cuentaContableCodigo)
        .map((l) => [l.claveSat, l])
    ).values()
  );

  return (
    <Card padding="lg">
      <div className="flex flex-col gap-4">
        <Group justify="space-between" align="flex-start">
          <div>
            <Heading order={5}>Líneas</Heading>
            <Text size="sm" c="dimmed" className="mt-0.5">
              Las líneas AUTOMÁTICAS pre-populadas solo permiten cambiar centro de costo e importe (decisión 16). Las MANUALES se editan al completo.
            </Text>
          </div>
          <BotonExportarExcel
            archivo="prorrateo"
            hoja="Líneas"
            filas={lineas}
            columnas={[
              { header: "#", value: (l) => l.numeroLinea },
              { header: "Tipo", value: (l) => l.tipo },
              { header: "Clave SAT", value: (l) => l.claveSat },
              { header: "Descripción", value: (l) => l.descripcion },
              { header: "CeCo", value: (l) => l.centroDeCostoCodigo },
              { header: "Cuenta", value: (l) => l.cuentaContableCodigo },
              { header: "Importe", value: (l) => l.importe },
              { header: "Indicador", value: (l) => l.indicador },
            ]}
          />
        </Group>
        <Divider />
        {!disabled && clavesSinCuenta.length > 0 && (
          <Alert variant="warning" icon={<IconAlertTriangle size={16} stroke={1.7} />}>
            <Stack gap="sm">
              <Text size="sm">
                {clavesSinCuenta.length === 1
                  ? "Una clave del CFDI no tiene cuenta contable configurada."
                  : `${clavesSinCuenta.length} claves del CFDI no tienen cuenta contable configurada.`}{" "}
                No puedes continuar hasta que el administrador la agregue al catálogo de cuentas por clave. Solicítala aquí:
              </Text>
              {clavesSinCuenta.map((l) => {
                const desc = descripcionDeClave(l.claveSat);
                return (
                  <div key={l.claveSat} className="flex flex-col gap-1">
                    <Text size="sm" fw={600} ff="mono">
                      {l.claveSat}
                      {desc && (
                        <Text span c="dimmed" fw={400}>
                          {" "}· {desc}
                        </Text>
                      )}
                    </Text>
                    <SolicitarAltaCatalogo
                      tipo="CuentaContablePorClave"
                      clave={l.claveSat}
                      descripcion={desc}
                      solicitudPagoId={solicitudId}
                      numeroSolicitudPago={numeroSolicitud}
                    />
                  </div>
                );
              })}
            </Stack>
          </Alert>
        )}
        <div className="overflow-x-auto">
          <Table verticalSpacing="sm" horizontalSpacing="xs">
            <Thead>
              <Tr>
                <Th>#</Th>
                <Th>Tipo</Th>
                <Th>Clave SAT</Th>
                <Th>Descripción</Th>
                <Th>CeCo</Th>
                <Th>Cuenta</Th>
                <Th align="right">Importe</Th>
                <Th>
                  <div className="flex items-center gap-1">
                    Indicador
                    {indicadorFijo && !disabled && (
                      <IconAction
                        label="Restablecer indicadores (pone a cada línea el indicador del Invoice)"
                        size="sm"
                        loading={restablecer.isPending}
                        onClick={() => restablecer.mutate()}
                      >
                        <IconRefresh size={14} stroke={1.8} />
                      </IconAction>
                    )}
                  </div>
                </Th>
                <Th />
              </Tr>
            </Thead>
            <Tbody>
              {lineas.map((l) => (
                <LineaRow key={l.id} linea={l} disabled={disabled} solicitudId={solicitudId} indicadorFijo={indicadorFijo} />
              ))}
            </Tbody>
          </Table>
        </div>
      </div>
    </Card>
  );
}
